/**
 * Submit cart data to procurement system via POST form.
 * This is required for cXML/OCI - simple GET redirects don't work
 * because the procurement system expects the cart data in the POST body.
 *
 * IMPORTANT: In iframe scenarios (Unite/Mercateo), we use target="_top"
 * to break out of the iframe and redirect the main window.
 *
 * @param {Object} options
 * @param {string} options.redirectUrl - The procurement system's return URL (HOOK_URL)
 * @param {string} options.cxmlResponse - cXML PunchOutOrderMessage (for cxml type)
 * @param {string} options.ociResponse - OCI URL-encoded parameters (for oci type)
 * @param {string} options.punchoutType - 'cxml' or 'oci'
 */
export function submitToProcurementSystem({
    redirectUrl,
    cxmlResponse,
    ociResponse,
    punchoutType
}) {
    // Mark transfer as in progress so auth guards can avoid redirect flicker to /login.
    sessionStorage.setItem('punchoutTransferInProgress', '1');

    console.log('📤 submitToProcurementSystem called:', {
        redirectUrl,
        punchoutType,
        hasCxmlResponse: !!cxmlResponse,
        hasOciResponse: !!ociResponse,
        cxmlLength: cxmlResponse?.length,
        ociLength: ociResponse?.length
    });

    // Create a hidden form for POST submission
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = redirectUrl;
    form.style.display = 'none';

    // CRITICAL: Set target="_top" to break out of iframe (Unite/Mercateo scenario)
    // Without this, the form would submit within the iframe, which doesn't work
    // because the procurement system expects the top-level window to navigate
    form.target = '_top';

    if (punchoutType === 'cxml' && cxmlResponse) {
        // For cXML: POST the XML data
        // Different procurement systems expect different field names:
        // - Mercateo/Unite: 'cxml-urlencoded' (URL-encoded) or 'cxml'
        // - SAP Ariba: 'cxml-urlencoded'
        // - Coupa: 'cxml-urlencoded'
        // - Ivalua: 'cxml' or 'cxml-urlencoded'

        // IMPORTANT (Mercateo/Unite): do NOT pre-encode here.
        // The browser will form-encode fields once during POST (application/x-www-form-urlencoded).
        // Pre-encoding would lead to double-encoding (%3C -> %253C) and can break parsing.
        const cxmlField = document.createElement('input');
        cxmlField.type = 'hidden';
        cxmlField.name = 'cxml-urlencoded';
        cxmlField.value = cxmlResponse;
        form.appendChild(cxmlField);

        // Also add raw cXML field as fallback (some systems prefer this)
        const cxmlRawField = document.createElement('input');
        cxmlRawField.type = 'hidden';
        cxmlRawField.name = 'cxml';
        cxmlRawField.value = cxmlResponse;
        form.appendChild(cxmlRawField);

    } else if (punchoutType === 'oci' && ociResponse) {
        // For OCI: Parse the URL-encoded response and add each field
        // OCI uses indexed parameters like NEW_ITEM-DESCRIPTION[1], NEW_ITEM-PRICE[1], etc.
        const params = new URLSearchParams(ociResponse);

        for (const [key, value] of params.entries()) {
            const field = document.createElement('input');
            field.type = 'hidden';
            field.name = key;
            field.value = value;
            form.appendChild(field);
        }
    }

    // Append form to body and submit
    document.body.appendChild(form);
    
    console.log('📤 Submitting form:', {
        action: form.action,
        method: form.method,
        target: form.target,
        fieldCount: form.elements.length
    });
    
    form.submit();

    // Note: form.submit() will navigate away from the page,
    // so cleanup is not strictly necessary, but good practice
    setTimeout(() => {
        if (form.parentNode) {
            form.parentNode.removeChild(form);
        }
    }, 1000);
}

/**
 * Fallback redirect using GET (when no cart data is present).
 * Attempts to use window.top for iframe scenarios.
 *
 * @param {string} redirectUrl - The URL to redirect to
 */
export function fallbackRedirect(redirectUrl) {
    try {
        // Use window.top for iframe scenarios (Unite/Mercateo)
        window.top.location.href = redirectUrl;
    } catch (e) {
        // Cross-origin iframe restriction - try regular redirect
        window.location.href = redirectUrl;
    }
}
